import { useState, useContext } from "react";
import { makeStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import { UserCtx } from "../../../interfaces";
import { UserContext } from "../../../context/UserContext";
import {
  Typography,
  Button,
  Stack,
  Box,
  CardHeader,
  Tooltip,
  IconButton,
  TextField,
  Link,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ASULogo from "../../../assets/asu-ked-logo.png";
import { CustomCheckBox } from "./CustomCheckBox";

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    backgroundColor: "transparent",
  },
  newPosOfDialog: {
    padding: "16px 0px 32px 0px",
    minHeight: 200,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: 15,
  },
  dialogMaxWidth: {
    maxWidth: 510,
  },
  paddingContainer: {
    padding: "0px 32px",
  },
  dialogTitle: {
    marginBottom: "24px",
  },
  dialogText: {
    marginBottom: "33px",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  buttonWrapper: {
    display: "flex",
    justifyContent: "flex-start",
  },
  bottomButton: {
    fontSize: "16px",
    borderRadius: "36px",
    borderColor: "#191919",
    paddingLeft: "24px",
    paddingRight: "24px",
    paddingTop: "12px",
    paddingBottom: "12px",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    marginRight: "16px",

    "&:hover": {
      cursor: "pointer",
    },
  },
  buttonNo: {
    width: 210,
    backgroundColor: "#D8D8D8",
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: "#C9C8C8",
    },
  },
  buttonYes: { width: 210, padding: 0 },
  bodyContainer: {
    padding: "0px 32px",
    maxHeight: "24em",
    overflowX: "hidden",
    overflowY: "auto",
  },
  bodyHeader: {
    fontWeight: theme.typography.fontWeightBold,
  },
  bodyText: {
    marginBottom: "1em",
  },
}));

export interface SurveyConsentModalProps {
  promptOpen: boolean;
  promptOnClose: () => void;
}

export const SurveyConsentModal = (props: SurveyConsentModalProps) => {
  const classes = useStyles();
  const { promptOnClose, promptOpen } = props;
  const userCtx = useContext(UserContext) as UserCtx;
  const [isAdult, setIsAdult] = useState<boolean | null>(false); // initialize as null to enable prompting student for age (consent/assent path). false will force it to assume all are <18 and skip
  const [nameInput, setNameInput] = useState<string>();
  const [contactConsent, setContactConsent] = useState<boolean>(false);
  const userSchool =
    userCtx.dbUser &&
    userCtx.dbUser.user_school &&
    userCtx.dbUser.user_school?.length &&
    userCtx.dbUser.user_school[0].school;
  const useBlueBottleVariant = userSchool && userSchool.metadata?.surveys_bluebottle;

  const studyTitle = useBlueBottleVariant
    ? "Study: The Impact of the MathSpine Intervention on ASU Math Courses"
    : "Study: Digit Courseware Fall 2023 - Spring 2024 Survey Study";

  const handleDismissPrompt = () => {
    promptOnClose();
    userCtx.upsertUserSurvey({
      consent_dismissals: (userCtx.dbUser?.user_survey?.consent_dismissals ?? 0) + 1,
    });
  };

  const handleAcceptPrompt = () => {
    promptOnClose();
    userCtx.upsertUserSurvey({
      consent: true,
      consent_date: new Date(),
      is_adult: isAdult ?? false,
      contact_consent: contactConsent,
    });
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContactConsent(e.target.checked);
  };

  // const consentLanguage = () => {
  //   return (
  //     <>
  //       <Box className={classes.bodyContainer}>
  //         <Typography
  //           component="div"
  //           variant="body1"
  //           className={classes.bodyHeader}
  //           sx={{ marginBottom: "1em" }}>
  //           <div>{studyTitle}</div>
  //         </Typography>
  //         <Typography component="div" variant="body2" className={classes.bodyText}>
  //           You are invited to participate in a research study about math anxiety, math
  //           motivation, and the use of adaptive courseware like the Digit. If you choose to participate in this study you will be asked to fill
  //           out short survey questionnaires five times throughout the next few months. The
  //           first of these surveys will be completed if you choose to consent. Afterwards
  //           you will receive prompts to complete additional surveys after you complete 20,
  //           and 40 problems in the tutor. A final survey will be administered at the end
  //           of your summer class. If you choose to participate in this study the
  //           researchers will also look at how you used the Digit,
  //           including which hints and feedback you viewed and which problems you submitted
  //           and whether or not they were correct or incorrect. We will also link your
  //           school test scores and grades to your survey and Digit.
  //         </Typography>
  //         <Typography component="div" variant="body2" className={classes.bodyText}>
  //           If you choose to participate in this study the researchers will also look at
  //           how you used the Digit,including which hints and feedback you
  //           viewed and which problems you submitted and whether or not they were correct
  //           or incorrect. We will also link your school test scores and grades to your
  //           survey and Digit data.
  //         </Typography>
  //         {/*  */}
  //         <Typography component="div" variant="body1" className={classes.bodyHeader}>
  //           RISKS AND BENEFITS:
  //         </Typography>
  //         <Typography component="div" variant="body2" className={classes.bodyText}>
  //           There are no foreseeable risks involved in participating in this study other
  //           than those encountered in day-to-day life. Your participation in this survey
  //           is voluntary. You may refuse to take part in the research or exit the survey
  //           at any time. You are free to decline to answer any particular question you do
  //           not wish to answer for any reason.
  //         </Typography>
  //         <Typography component="div" variant="body2" className={classes.bodyText}>
  //           Although there are no direct benefits to you, participation in this research
  //           study could expand our understanding of how adaptive courseware influences
  //           math anxiety and math motivation.
  //         </Typography>
  //         {/*  */}
  //         <Typography component="div" variant="body1" className={classes.bodyHeader}>
  //           DATA STORAGE TO PROTECT CONFIDENTIALITY:
  //         </Typography>
  //         <Typography component="div" variant="body2" className={classes.bodyText}>
  //           Your survey answers and your tutor use data will be stored in a password
  //           protected electronic format. Additionally, de-identified data collected as a
  //           part of this study will be shared with others (for example, investigators or
  //           industry partners) for future research purposes or to improve the Digit.
  //         </Typography>
  //         {/*  */}
  //         <Typography component="div" variant="body1" className={classes.bodyHeader}>
  //           TIME INVOLVEMENT:
  //         </Typography>
  //         <Typography component="div" variant="body2" className={classes.bodyText}>
  //           Your participation will take 20 minutes in total, with approximately 5 minutes
  //           per survey you submit.
  //         </Typography>
  //         {/*  */}
  //         <Typography component="div" variant="body1" className={classes.bodyHeader}>
  //           HOW WILL RESULTS BE USED:
  //         </Typography>
  //         <Typography component="div" variant="body2" className={classes.bodyText}>
  //           The results of the study will be used for scholarly purposes and may be shared
  //           with stakeholders in aggregate form only to improve the Digit. The results from the study might be presented in conferences, published
  //           in journals or articles for educational purposes, but none of your personal
  //           information will be released.
  //         </Typography>
  //         {/*  */}
  //         <Typography component="div" variant="body1" className={classes.bodyHeader}>
  //           QUESTIONS / CONCERNS:
  //         </Typography>
  //         <Typography component="div" variant="body2" className={classes.bodyText}>
  //           If at any time you have comments, or concerns regarding this research study
  //           you can contact the study PI, Dr. Alysha Ramirez-Hall via email at
  //           arhall16@asu.edu or the Co-PI Margeaux Ciraolo at mciraol@asu.edu or by phone
  //           at (480) 862-9479.
  //         </Typography>
  //         <Typography component="div" variant="body2" className={classes.bodyText}>
  //           If you have any questions about your rights as a subject/participant in this
  //           research, or if you feel you have been placed at risk, you can contact the
  //           Chair of the Human Subjects Institutional Review Board, through the ASU Office
  //           of Research Integrity and Assurance, at (480) 965-6788.
  //         </Typography>
  //         {/*  */}
  //         <Typography component="div" variant="body1" className={classes.bodyHeader}>
  //           By typing your name and selecting “I agree” below you indicate that:
  //         </Typography>
  //         <Typography component="div" variant="body2" className={classes.bodyText}>
  //           <div>You have read the above information </div>
  //           <div>You voluntarily agree to participate </div>
  //           <div>You are at least 18 years of age.</div>
  //         </Typography>
  //       </Box>
  //     </>
  //   );
  // };

  const assentLanguage = () => {
    return (
      <>
        <Box className={classes.bodyContainer}>
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            <div>{studyTitle}</div>
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            You are invited to participate in a research study on how middle school and
            high students feel about math. In addition to asking questions about how you
            feel about math, we will also look at how you used the Digit to try and
            understand if how you think/feel about math is related to how you solve math
            problems. Your Digit and survey data will also be linked to your test scores
            and grades from school.
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            Your participation in this study is voluntary. You may refuse to take part in
            the research or exit the survey at any time. If you agree to be in this study
            you will be asked survey questions about your feelings about math while you
            use the tutor. Your participation will take about 45 minutes in total, with
            the survey at the beginning and end of the study taking approximately 10
            minutes each, while the surveys that you will take in the middle of the school
            year should take about 5 minutes each.
          </Typography>
          {/*  */}
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            DATA STORAGE TO PROTECT CONFIDENTIALITY:
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            Only the Data Catalytics and Action Lab research team will have access to all
            survey responses from all parties (parents, students, teachers). Responses
            will be kept confidential. We will not collect your IP address as part of this
            survey. The results of this survey may be used in reports, presentations, or
            publications but names will not be used. Results will only be shared in the
            aggregate form. We will not share raw data from this survey with anyone
            outside the Data Catalytics and Action Lab research team.
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            In regard to classroom observations, the information collected will be notes.
            The notes will be expanded/modified into a one-page narrative. The narrative
            will then be sent to the participating teacher for review. Each participating
            teacher will have one week to review the narrative and agree with its
            contents, suggest edits, or request a conversation to clarify the narrative
            with the researcher. We want each participating teacher to approve the
            narrative to build confidence and trust in the research. Discrepancies will be
            discussed by the researchers and the teacher and be resolved together. Only
            the members of the research team will have access to non-anonymous notes taken
            during your child’s class. All data will be stored on ASU secure servers that
            are firewalled, password protected, and require SSL access.
          </Typography>
          {/*  */}
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            HOW WILL RESULTS BE USED:
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            Findings from surveys and classroom observations may be published, but only in
            de-identified and aggregate form. No personal identifiers will ever be shown
            in presentations or publications resulting from this research. Each
            participant will be assigned a random and unique identifying number (ID such
            as #XYZ) and when data is shared, the researcher(s) will refer to that
            participant by that number and remove any private information. The original
            files will have the personally identifiable information and the assigned
            participant number.
          </Typography>
          {/*  */}
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            QUESTIONS / CONCERNS:
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            If you have any questions concerning the research study, please contact the
            study’s Principal Investigators (PIs):
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            Dr. Alysha Ramirez Hall, Sr. Research Scientist, Data Catalytics and Action
            Lab:&nbsp;<Link href="mailto:arhall16@asu.edu">arhall16@asu.edu</Link>
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            Dr. Rachel L Griffin, Sr. Research Scientist, Data Catalytics and Action Lab:
            &nbsp;
            <Link href="mailto:Rachel.L.Griffin@asu.edu">Rachel.L.Griffin@asu.edu</Link>
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            If you have any questions about your rights as a subject/participant in this
            research, or if you feel you have been placed at risk, you can contact the
            Chair of the Human Subjects Institutional Review Board, through the ASU Office
            of Research Integrity and Assurance, at (480) 965-6788.
          </Typography>
          <Stack
            direction="row"
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            sx={{ margin: "32px 0px" }}>
            <img
              style={{ width: "100px", height: "auto" }}
              src={ASULogo}
              alt="ASU KED Logo"></img>
            <Box
              sx={{
                border: "1px solid black",
                fontSize: "0.8em",
                justifySelf: "center",
                padding: "8px",
              }}>
              ASU IRB IRB # STUDY00018481 | Approval Period 8/28/2023
            </Box>
            <Box sx={{ width: "100px" }}></Box>
          </Stack>
          {/*  */}
          <Divider sx={{ marginBottom: "32px" }} />
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            Selecting "I Agree" and typing your name below indicates that:
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            <ol style={{ paddingLeft: "16px" }}>
              <li>
                My participation in this project is voluntary and I have been told that I
                may stop my participation in this study at any time. If I choose not to
                participate, it will not affect my grade, in any way.
              </li>
              <li>
                I have been informed that my teacher has given permission for me to
                participate in a study concerning middle school and high school students'
                attitude toward learning math.
              </li>
              <li>
                Clicking the "I Agree" and typing my name below button below represents my
                permission to participate in this study.
              </li>
            </ol>
          </Typography>
        </Box>
      </>
    );
  };

  const assentLanguageBB = () => {
    return (
      <>
        <Box className={classes.bodyContainer}>
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            <div>{studyTitle}</div>
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            You are invited to participate in a research study on how college aged
            students feel about math. In addition to asking questions about how you feel
            about math, we will also look at how you use different aspects of the course
            (gapped notes, Digit homework system) to try and understand if how you
            think/feel about math is related to how you solve math problems. Your Digit
            homework system data and survey data will also be linked to your test scores
            and grades from the course.
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            Your participation in this study is voluntary. All participants must be 18
            years or older. You may refuse to take part in the research or exit the survey
            at any time. If you agree to be in this study you will be asked survey
            questions about your feelings about math while you use the homework system.
            Your participation will take about 20 minutes in total, with the survey at the
            beginning and end of the study taking approximately 10 minutes each.
          </Typography>
          {/*  */}
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            DATA STORAGE TO PROTECT CONFIDENTIALITY:
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            Only the Data Catalytics and Action Lab research team will have access to all
            survey responses from all parties (students, teachers). Responses will be kept
            confidential. We will not collect your IP address as part of this survey. The
            results of this survey may be used in reports, presentations, or publications
            but names will not be used. Results will only be shared in the aggregate form.
            We will not share raw data from this survey with anyone outside the Data
            Catalytics and Action Lab research team. De-identified data collected as a
            part of current study may be shared with others (e.g., investigators or
            industry partners) for future research purposes.
          </Typography>
          {/*  */}
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            HOW WILL RESULTS BE USED:
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            Findings from surveys may be published, but only in de-identified and
            aggregate form. No personal identifiers will ever be shown in presentations or
            publications resulting from this research. Each participant will be assigned a
            random and unique identifying number (ID such as #XYZ) and when data is
            shared, the researcher(s) will refer to that participant by that number and
            remove any private information. The original files will have the personally
            identifiable information and the assigned participant number.
          </Typography>
          {/*  */}
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            QUESTIONS / CONCERNS:
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            If you have any questions concerning the research study, please contact the
            study’s Principal Investigators (PIs):
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            Dr. Alysha Ramirez Hall, Sr. Research Scientist, Data Catalytics and Action
            Lab:&nbsp;<Link href="mailto:arhall16@asu.edu">arhall16@asu.edu</Link>
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            Dr. Rachel L Griffin, Sr. Research Scientist, Data Catalytics and Action Lab:
            &nbsp;
            <Link href="mailto:Rachel.L.Griffin@asu.edu">Rachel.L.Griffin@asu.edu</Link>
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            If you have any questions about your rights as a subject/participant in this
            research, or if you feel you have been placed at risk, you can contact the
            Chair of the Human Subjects Institutional Review Board, through the ASU Office
            of Research Integrity and Assurance, at (480) 965-6788.
          </Typography>
          <Stack
            direction="row"
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            sx={{ margin: "32px 0px" }}>
            <img
              style={{ width: "100px", height: "auto" }}
              src={ASULogo}
              alt="ASU KED Logo"></img>
            <Box
              sx={{
                border: "1px solid black",
                fontSize: "0.8em",
                justifySelf: "center",
                padding: "8px",
              }}>
              ASU IRB IRB # STUDY00018640 | Approval Period 9/27/2023
            </Box>
            <Box sx={{ width: "100px" }}></Box>
          </Stack>
          {/*  */}
          <Divider sx={{ marginBottom: "32px" }} />
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            Selecting "I Agree" and typing your name below indicates that:
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            <ol style={{ paddingLeft: "16px" }}>
              <li>
                My participation in this project is voluntary and I have been told that I
                may stop my participation in this study at any time. If I choose not to
                participate, it will not affect my grade, in any way.
              </li>
              <li>
                I understand that while this project is a course requirement, I do not
                have to agree to allow my data to be used for research purposes.
              </li>
              <li>
                Clicking the "I Agree" and typing my name below button below represents my
                permission to participate in this study and have data used for research
                purposes.
              </li>
            </ol>
          </Typography>
          <Typography component="div" variant="body1" className={classes.bodyHeader}>
            By checking the box below you are agreeing to be contacted for follow up
            interview or focus group participation, which is eligible for compensation.
          </Typography>
          <Typography component="div" variant="body2" className={classes.bodyText}>
            <ul style={{ paddingLeft: "16px" }}>
              <li>
                Yes, I may be contacted for additional interview or user experience
                research
              </li>
            </ul>
          </Typography>
        </Box>
      </>
    );
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth={isAdult === null ? "sm" : "md"}
        classes={{
          paper: `${classes.newPosOfDialog} ${
            isAdult === null ? classes.dialogMaxWidth : ""
          }`,
        }}
        aria-labelledby="simple-dialog-title"
        open={promptOpen}>
        <CardHeader
          className={classes.paddingContainer}
          title={
            <Typography component="div" variant="h4" className={classes.dialogTitle}>
              {isAdult === null ? (
                "Help us conduct research on the tutor!"
              ) : (
                <>
                  <div>{studyTitle}</div>
                </>
              )}
            </Typography>
          }
          action={
            <Tooltip
              style={{ zIndex: "1200 !important" }}
              placement="bottom-end"
              title="Close"
              arrow
              role="tooltip"
              describeChild>
              <IconButton
                onClick={handleDismissPrompt}
                aria-label="close consent modal"
                className={classes.closeButton}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          }
        />

        {isAdult === null ? (
          <>
            <Box className={classes.paddingContainer}>
              <Typography component="div" variant="body2" className={classes.dialogText}>
                Are you over 18?
              </Typography>
              <Stack direction="row" justifyContent="space-between">
                <Button
                  onClick={() => setIsAdult(false)}
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.buttonNo}>
                  No
                </Button>
                <Button
                  onClick={() => setIsAdult(true)}
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.buttonYes}>
                  Yes
                </Button>
              </Stack>
            </Box>
          </>
        ) : (
          <>
            <Typography component="div" variant="body2" className={classes.dialogText}>
              {/* Commented out and defaulted to assentLanguage as no students will participate in this survey */}
              {/* {isAdult ? consentLanguage() : assentLanguage()} */}
              {useBlueBottleVariant ? assentLanguageBB() : assentLanguage()}
            </Typography>

            <Stack
              direction="row"
              justifyContent="center"
              className={classes.paddingContainer}
              sx={{ marginBottom: "16px" }}>
              <Stack direction="column" justifyContent="center" alignItems="center">
                <Typography
                  component="div"
                  variant="body2"
                  style={{ marginBottom: "8px" }}>
                  Enter your full name ({userCtx.dbUser?.full_name ?? ""}) exactly as
                  shown below
                </Typography>
                <TextField
                  sx={{ marginBottom: "8px", width: 300 }}
                  label="Full Name"
                  variant="outlined"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder={userCtx.dbUser?.full_name ?? ""}
                />
                {useBlueBottleVariant && (
                  <Stack
                    direction="row"
                    justifyContent="center"
                    className={classes.paddingContainer}>
                    <CustomCheckBox
                      label="Yes, I may be contacted"
                      checked={contactConsent}
                      onChange={handleCheckbox}
                      emitDataLayer={(checked: boolean) => {}}
                    />
                  </Stack>
                )}
              </Stack>
            </Stack>
            <Stack
              direction="row"
              justifyContent="center"
              className={classes.paddingContainer}>
              <Button
                onClick={handleAcceptPrompt}
                variant="contained"
                color="primary"
                size="large"
                disabled={
                  !(nameInput?.toLowerCase() === userCtx.dbUser?.full_name?.toLowerCase())
                }
                className={classes.buttonYes}>
                I agree
              </Button>
            </Stack>
          </>
        )}
      </Dialog>
    </>
  );
};
